import React from 'react';
import { useIntl } from 'react-intl';
import { Container, Row, Col } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { SMLContainer } from '../smartLinks/smlContainer/SMLContainer';
import { SMLBreadcrumbs } from '../smartLinks/smlBreadcrumbs/SMLBreadcrumbs';
import { SMLRouteNames, SMLRoutes } from '../../routing/sml/SMLRoutes';
import { useTrackDetails } from './hooks/useTrackDetails';
import { LoadingView } from '../common/views/LoadingView';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { SMLDatePicker } from './components/SMLDatePicker/SMLDatePicker';
import { SMLCountrySelector } from './components/SMLCountrySelector/SMLCountrySelector';
import { TrackStatsRow } from './components/TrackStatsRow/TrackStatsRow';
import { TrackPositionChangeChart } from './components/TrackPositionChangeChart/TrackPositionChangeChart';
import { TrackPlaylistScoreChart } from './components/TrackPlaylistScoreChart/TrackPlaylistScoreChart';
import { TrackTopPlaylistsCard } from './components/TrackTopPlaylistsCard/TrackTopPlaylistsCard';
import { TrackRecentPlaylistChangesCard } from './components/TrackRecentPlaylistChangesCard/TrackRecentPlaylistChangesCard';
import { FollowButton } from './components/FollowButton/FollowButton';
import { SharePubliclyButton } from './components/SharePublicly/SharePubliclyButton';
import { SMLLink } from './components/SMLLink/SMLLink';
import { datePickerParamName } from './components/SMLDatePicker/consts';
import { useSMLDatePickerDateParam } from './hooks/useSMLDatePickerDateParam';
import { SMLImage } from './components/SMLImage';
import { PublicModeError } from './components/PublicModeError/PublicModeError';
import { useShareTokenContext } from './contexts/ShareTokenContext';
import { useEntityIdParam } from './hooks/useEntityIdParam';
import { useCanViewListeningInsights } from './hooks/useCanViewListeningInsights';
import { TrackListeningScoreChart } from './components/TrackListeningScoreChart/TrackListeningScoreChart';
import { formatDuration } from './utils/trackFormatting';
import { TrackServiceLinks } from './components/TrackServiceLinks/TrackServiceLinks';
import { CopyTrackLinkButton } from './components/CopyTrackLinkButton/CopyTrackLinkButton';
import { TrackAboutCard } from './components/TrackAboutCard/TrackAboutCard';
import { TrackAudioProfileCard } from './components/TrackAudioProfileCard/TrackAudioProfileCard';
import { ChartsBackLink } from './charts/ChartsBackLink';
import { useChartsDetailCrumbs } from './charts/useChartsDetailCrumbs';
import { useMobilePageTitle } from './navigation/hooks/useMobilePageTitle';

const SMLTrackDetailsComponent: React.FC = () => {
  const intl = useIntl();
  const { payload, isPublicMode } = useShareTokenContext();
  const trackId = useEntityIdParam('track');
  const { selectedDateValue } = useSMLDatePickerDateParam();

  const {
    data: trackData,
    isLoading,
    error,
    refetch,
  } = useTrackDetails({
    trackId: trackId ?? '',
  });
  // Design b4: the mobile top bar names the entity of this deep page.
  useMobilePageTitle(trackData?.mainName);

  const canViewListeningInsights = useCanViewListeningInsights();

  const crumbName =
    trackData?.mainName ||
    intl.formatMessage({ id: 'app.sml.breadcrumbs.track-details', defaultMessage: 'Track Details' });
  const breadcrumbs = useChartsDetailCrumbs(SMLRouteNames.tracks, crumbName, isPublicMode);
  // design navigation-v2 e 5c: a page under Charts has a back link; a public share link has none.
  const back = isPublicMode ? null : <ChartsBackLink tab={SMLRouteNames.tracks} />;

  if (isPublicMode && payload?.t !== 'track') {
    return (
      <SMLContainer publicMode header={null}>
        <PublicModeError />
      </SMLContainer>
    );
  }

  if (trackId === null) {
    return (
      <SMLContainer
        publicMode={isPublicMode}
        header={
          <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
            <div className="d-flex align-items-center gap-2">
              {!isPublicMode && <SMLCountrySelector />}
              <SMLDatePicker showRangePresets />
            </div>
          </SMLBreadcrumbs>
        }
      >
        {back}
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>{intl.formatMessage({ id: 'app.sml.track.details.no-id', defaultMessage: 'No track ID provided' })}</p>
        </div>
      </SMLContainer>
    );
  }

  if (error && isPublicMode) {
    return (
      <SMLContainer publicMode header={null}>
        <PublicModeError />
      </SMLContainer>
    );
  }

  if (error) {
    return (
      <SMLContainer
        publicMode={isPublicMode}
        header={
          <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
            <div className="d-flex align-items-center gap-2">
              {!isPublicMode && <SMLCountrySelector />}
              <SMLDatePicker showRangePresets />
            </div>
          </SMLBreadcrumbs>
        }
      >
        {back}
        <ErrorView
          error={error}
          retry={() => {
            refetch().catch(console.error);
          }}
        />
      </SMLContainer>
    );
  }

  if (isLoading) {
    return (
      <SMLContainer
        publicMode={isPublicMode}
        header={
          <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
            <div className="d-flex align-items-center gap-2">
              {!isPublicMode && <SMLCountrySelector />}
              <SMLDatePicker showRangePresets />
            </div>
          </SMLBreadcrumbs>
        }
      >
        {back}
        <LoadingView />
      </SMLContainer>
    );
  }

  return (
    <SMLContainer
      publicMode={isPublicMode}
      header={
        <SMLBreadcrumbs breadcrumbs={breadcrumbs} variant="eyebrow" hideCrumbsOnPhone={!isPublicMode}>
          <SMLDatePicker showRangePresets />
        </SMLBreadcrumbs>
      }
    >
      {back}
      <Container fluid>
        {/* Track Name Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-3">
                <SMLImage
                  src={trackData?.imageUrl}
                  alt={trackData?.mainName || 'Track'}
                  width={64}
                  makeSquareFit
                  className="rounded"
                  style={{
                    width: '64px',
                    height: '64px',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
                <div>
                  <h1 className="mb-0">{trackData?.mainName}</h1>
                  {trackData?.mainArtistName && trackData?.artistMaid && (
                    <SMLLink
                      to={SMLRoutes.artistDetails}
                      params={{ artistId: trackData.artistMaid, [datePickerParamName]: selectedDateValue }}
                      className="text-decoration-none"
                    >
                      <h5 className="text-muted mb-0 mt-2" style={{ cursor: 'pointer' }}>
                        {trackData.mainArtistName}
                      </h5>
                    </SMLLink>
                  )}
                  <div className="d-flex align-items-center gap-2 mt-1 text-muted small">
                    {formatDuration(trackData?.durationMs ?? null) !== null && (
                      <span>{formatDuration(trackData?.durationMs ?? null)}</span>
                    )}
                    {trackData?.explicit === true && (
                      <span
                        className="badge bg-secondary"
                        title={intl.formatMessage({
                          id: 'app.sml.track.details.explicitTitle',
                          defaultMessage: 'Explicit',
                        })}
                      >
                        {intl.formatMessage({ id: 'app.sml.track.details.explicit', defaultMessage: 'E' })}
                      </span>
                    )}
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    {trackData?.serviceLinks !== undefined && trackData.serviceLinks.length > 0 && (
                      <TrackServiceLinks links={trackData.serviceLinks} />
                    )}
                    <CopyTrackLinkButton linkSlug={trackData?.linkSlug} />
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                {!isPublicMode && (
                  <SharePubliclyButton
                    type="track"
                    id={trackId}
                    shareTitle={intl.formatMessage(
                      {
                        id: 'app.sml.sharePublicly.title.track',
                        defaultMessage: '{trackName} by {artistName} on MUSIC24',
                      },
                      {
                        trackName: trackData?.mainName ?? '',
                        artistName: trackData?.mainArtistName ?? '',
                      }
                    )}
                    name={trackData?.mainName}
                    coverUrl={trackData?.imageUrl ?? undefined}
                    subtitle={
                      trackData?.mainArtistName !== undefined && trackData.mainArtistName !== ''
                        ? intl.formatMessage(
                            {
                              id: 'app.sml.sharePublicly.subtitle.track',
                              defaultMessage: '{artist} · Track',
                            },
                            { artist: trackData.mainArtistName }
                          )
                        : intl.formatMessage({ id: 'app.sml.sharePublicly.type.track', defaultMessage: 'Track' })
                    }
                  />
                )}
                <FollowButton trackId={trackId} variant="button" size="lg" />
              </div>
            </div>
          </Col>
        </Row>

        {/* About + Audio Profile Row */}
        <Row className="mb-2">
          <Col md={6} className="mb-3">
            <TrackAboutCard album={trackData?.album ?? null} isrc={trackData?.isrc ?? null} />
          </Col>
          <Col md={6} className="mb-3">
            <TrackAudioProfileCard
              audioFeatures={trackData?.audioFeatures ?? null}
              popularity={trackData?.popularity ?? null}
            />
          </Col>
        </Row>

        {/* Stats Cards Row */}
        <TrackStatsRow />

        {/* Charts Row */}
        <Row>
          <Col md={6} className="mt-4">
            <TrackPositionChangeChart />
          </Col>
          <Col md={6} className="mt-4">
            <TrackPlaylistScoreChart />
          </Col>
          {canViewListeningInsights && (
            <Col md={6} className="mt-4">
              <TrackListeningScoreChart />
            </Col>
          )}
        </Row>

        {/* Playlist lists row */}
        <Row>
          <Col xl={6} className="mt-4">
            <TrackTopPlaylistsCard />
          </Col>
          <Col xl={6} className="mt-4">
            <TrackRecentPlaylistChangesCard />
          </Col>
        </Row>
      </Container>
    </SMLContainer>
  );
};

export const SMLTrackDetails = TypedMemo(SMLTrackDetailsComponent);
